// src/routes/employeeRoutesConfig.ts
import React, { lazy } from 'react';
import { Navigate } from 'react-router-dom';

// Attendance is consolidated into a single shared /attendance workspace (see
// routes/index.tsx). Legacy per-page paths redirect into the right workspace tab.
const redirectTo = (to: string) => () => React.createElement(Navigate, { to, replace: true });

const SalaryHistoryTimeline = lazy(() => import('../../pages/SalaryRevision/SalaryHistoryTimeline'));
const MyPolicies = lazy(() => import('../../pages/Policies/MyPolicies'));

const EmployeeDashboard = lazy(() => import('../../pages/Dashboard/Employee'));
const Payslips = lazy(() => import('../../pages/HrPayroll/Payslips'));
const TaxDocuments = lazy(() => import('../../pages/HrPayroll/TaxDocuments'));
const Assets = lazy(() => import('../../pages/Assests/Assets'));
const Attendance = lazy(() => import('../../pages/HrPayroll/Attendance'));
const OvertimeSubmission = lazy(() => import('../../pages/HrPayroll/OvertimeSubmission'));
const WFHRequests = lazy(() => import('../../pages/HrPayroll/WFHRequests'));
const Leaves = lazy(() => import('../../pages/HrPayroll/Leaves'));
const BankDetails = lazy(() => import('../../pages/SelfService/BankDetails'));
const AddBankDetails = lazy(() => import('../../pages/SelfService/AddBankDetails'));
const EmploymentHistory = lazy(() => import('../../pages/SelfService/EmploymentHistory'));
const PersonalInformation = lazy(() => import('../../pages/SelfService/PersonalInformation'));
const ChangePassword = lazy(() => import('../../pages/SelfService/ChangePassword'));
const ResignationForm = lazy(() => import('../../pages/Resignation/EmployeeResignationWorkspace'));
const SelfEvaluation = lazy(() => import('../../pages/SelfEvaluation/SelfEvaluation'));
const Kudos = lazy(() => import('../../pages/Recogniation & Engagement/RecognitionFeed'));
const PulseSurvey = lazy(() => import('../../pages/Recogniation & Engagement/PulseSurvey'));
const Procurement = lazy(() => import('../../pages/Procurement/Procurement'));
const HRRequest = lazy(() => import('../../pages/HrRequest/HrRequest'));
const Warnings = lazy(() => import('../../pages/Warnings/Warnings'));
const HrDocuments = lazy(() => import('../../pages/HrDocuments/HrDocuments'));
const GeneratePayslip = lazy(() => import('../../pages/HrPayroll/GeneratePayslip'));
const NotFound = lazy(() => import('../../NotFound'));
const ResignationManagement = lazy(() => import('../../pages/Resignation/ManagerApprovalInbox'));
const Reimbursement = lazy(() => import('../../pages/Reibursements/Reimbursement'));
const EmployeeScorecard = lazy(() => import('../../pages/Kpis/EmployeeScorecard'));
const IssueTicketsPage = lazy(() => import('../../pages/IssueTickets/IssueTicketsPage'));
const Appraisals = lazy(() => import('../../pages/AppraisalsManagement/Appraisals/Appraisals'));
const AppraisalDetail = lazy(() => import('../../pages/AppraisalsManagement/Appraisals/AppraisalDetail'));
const ManagerPendingAppraisals = lazy(() => import('../../pages/AppraisalsManagement/Appraisals/ManagerPendingAppraisals'));
const PeerFeedbackPage = lazy(() => import('../../pages/AppraisalsManagement/AppraisalsFeedback/AppraisalsFeedback'));
const AttendanceRegularization = lazy(() => import('../../pages/AttendanceRegularization/AttendanceRegularization'));
const ManagerAttendanceRequests = lazy(() => import('../../pages/AttendanceRegularization/ManagerAttendanceRequests'));
const MyLoansPage = lazy(() => import('../../pages/Loans/MyLoansPage'));
const MyDocuments = lazy(() => import('../../pages/EmployeeDocuments/MyDocuments'));



const employeeRoutesConfig = [
  { path: '/v1/employee/dashboard', component: EmployeeDashboard },
  { path: '/v1/hr-payroll/payslip', component: Payslips },
  { path: '/v1/hr-payroll/tax-documents', component: TaxDocuments },
  // Legacy attendance paths → shared /attendance workspace (defined in routes/index.tsx)
  { path: '/v1/hr-payroll/leave-requests', component: redirectTo('/attendance?tab=requests') },
  { path: '/v1/hr-payroll/attendance', component: redirectTo('/attendance?tab=overview') },
  { path: '/v1/hr-payroll/overtime-submission', component: redirectTo('/attendance?tab=requests') },
  { path: '/v1/hr-payroll/wfh-requests', component: redirectTo('/attendance?tab=requests') },
  { path: '/v1/self-service/bank-details', component: BankDetails },
  { path: '/v1/self-service/add-bank-details', component: AddBankDetails },
  { path: '/v1/self-service/employment-history', component: EmploymentHistory },
  { path: '/v1/self-service/personal-info', component: PersonalInformation },
  { path: '/v1/self-service/change-password', component: ChangePassword },
  { path: '/v1/resignation/resignation-form', component: ResignationForm },
  // BUG5: Old /self-evaluation was a disconnected system (question-bank). Kept for safety but
  // the REAL self-eval is inside AppraisalDetail via PUT /Appraisals/self.
  // Employees should use /v1/employee/appraisals to access their appraisal and complete self-eval.
  { path: '/v1/employee/self-evaluation', component: SelfEvaluation },
  { path: '/v1/employee/appraisals', component: Appraisals },
  // IMPORTANT: Specific paths MUST come before :id wildcard in React Router
  // BUG4: Peer 360 Feedback page — employees give feedback to colleagues
  { path: '/v1/employee/appraisals/peer-feedback', component: PeerFeedbackPage },
  // BUG3: Fixed path — was /v1/hr/ (HR-protected) but managers are Employee role
  { path: '/v1/employee/appraisals/pending/manager', component: ManagerPendingAppraisals },
  // :id must be LAST (catches anything that didn't match above)
  { path: '/v1/employee/appraisals/:id', component: AppraisalDetail },
  { path: '/v1/hr-documents', component: HrDocuments },
  { path: '/v1/hr-payroll/generate-payslip', component: GeneratePayslip },
  { path: '/v1/resignation-management', component: ResignationManagement },
  { path: '/v1/reimbursement-requests', component: Reimbursement },
  { path: '/v1/kpis/scorecard', component: EmployeeScorecard },
  { path: '/v1/issue-tickets', component: IssueTicketsPage },
  { path: '/v1/employee/assets-management', component: Assets },
  { path: '/v1/recognition-engagement/kudos-wall', component: Kudos },
  { path: '/v1/recognition-engagement/pulse-surveys', component: PulseSurvey },
  { path: '/v1/hr-request', component: HRRequest },
  { path: '/v1/employee/warnings', component: Warnings },

  // MANAGED HR & PAYROLL (manager "all" views) → workspace Team Approvals tab
  { path: '/v1/leave-requests', component: redirectTo('/attendance?tab=team-approvals') },
  { path: '/v1/overtime-requests', component: redirectTo('/attendance?tab=team-approvals') },
  { path: '/v1/wfh', component: redirectTo('/attendance?tab=team-approvals') },

  // Attendance Regularization → workspace
  { path: '/v1/hr-payroll/attendance-regularization', component: redirectTo('/attendance?tab=requests') },
  { path: '/v1/hr-payroll/attendance-requests/pending/manager', component: redirectTo('/attendance?tab=team-approvals') },

  // Loans
  { path: '/v1/hr-payroll/my-loans', component: MyLoansPage },

  // My Documents — employee views their own uploaded HR documents
  { path: '/v1/self-service/my-documents', component: MyDocuments },

  // Salary History — read-only self-service view
  { path: '/v1/self-service/salary-history', component: SalaryHistoryTimeline },
  { path: '/v1/my-policies', component: MyPolicies },

  // Wildcard NotFound routes for section-specific 404s
  { path: '/v1/employee/*', component: NotFound },
  { path: '/v1/hr-payroll/*', component: NotFound },
  { path: '/v1/self-service/*', component: NotFound },
  { path: '/v1/resignation/*', component: NotFound },
  { path: '/v1/recognition-engagement/*', component: NotFound },
];

export default employeeRoutesConfig;
